import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ControlId, ControlState } from '@elutide/shared';
import { api, ApiProblem } from '@/lib/api';
import { qk } from '@/lib/keys';
import { fullTimestamp, relativeAge } from '@/lib/format';
import {
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  Pill,
  Skeleton,
  StepUpGate,
  useToast,
} from '@/components';

interface ControlsResponse {
  gate: ControlState;
  bogo: ControlState;
}

interface ControlCopy {
  title: string;
  what: string;
  effects: readonly string[];
}

const COPY: Record<ControlId, ControlCopy> = {
  gate: {
    title: 'Login gate',
    what: 'Requires a visitor to pass /enter before they can shop.',
    effects: [
      'Every storefront visitor is redirected to /enter until they pass it',
      'Existing sessions are unaffected',
      'The change is verified against the live storefront within 30 seconds',
    ],
  },
  bogo: {
    title: 'BOGO sale',
    what: 'Drives the pricing engine, the homepage hero, the banner and the coupon pause.',
    effects: [
      'Pricing changes on the live storefront immediately',
      'The hero and the banner appear or disappear',
      'Exempt coupons are paused or resumed',
      'Carts already holding BOGO items reprice at checkout',
    ],
  },
};

/**
 * Settings > Controls. The only place Gate and BOGO exist.
 *
 * They left the header on purpose. Flipping one changes the live storefront, so it takes the
 * full ceremony: owner only, a fresh passkey step-up regardless of age, a reason, typing ON
 * or OFF exactly, a 60 second cooldown, an append-only audit row, a Slack line, and a
 * storefront probe afterwards that proves it took. The page shows the verified state, so it
 * never claims ON while the store says otherwise.
 */
export function Controls() {
  const client = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<{ control: ControlId; next: boolean } | null>(null);

  const query = useQuery({
    queryKey: qk.settings('controls'),
    queryFn: () => api.get<ControlsResponse>('/settings/controls'),
    // Read live from WordPress through the bridge, never cached.
    staleTime: 0,
    refetchInterval: 30_000,
  });

  const toggle = useMutation({
    mutationFn: ({
      control,
      next,
      reason,
    }: {
      control: ControlId;
      next: boolean;
      reason: string;
    }) =>
      api.post<ControlState>(`/settings/controls/${control}`, {
        enabled: next,
        typed_confirm: next ? 'ON' : 'OFF',
        reason,
      }),
    onSuccess: (state) => {
      void client.invalidateQueries({ queryKey: qk.settings('controls') });
      toast.push({
        tone: state.verification_ok === false ? 'warn' : 'ok',
        message:
          state.verification_ok === false
            ? 'The flip was recorded but the storefront did not confirm it. A critical inbox item was raised.'
            : 'Done, verified on the storefront, and mirrored to Slack.',
      });
    },
    onError: (error) =>
      toast.push({
        tone: 'crit',
        message: error instanceof ApiProblem ? error.message : 'The control did not change.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      }),
  });

  if (query.isLoading) {
    return (
      <Card title="Storefront controls">
        <Skeleton shape="card" lines={2} label="Loading controls" />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card title="Storefront controls">
        <ErrorState
          message={
            query.error instanceof ApiProblem
              ? query.error.message
              : 'The control states could not be read from the storefront.'
          }
          requestId={query.error instanceof ApiProblem ? query.error.shortRequestId : null}
          onRetry={() => void query.refetch()}
        />
        <p className="o-muted">
          When the live state cannot be read, no switch is shown. A switch that guesses is
          worse than no switch.
        </p>
      </Card>
    );
  }

  const controls: readonly ControlId[] = ['gate', 'bogo'];
  const active = pending ? query.data[pending.control] : null;

  return (
    <>
      <Card
        title="Storefront controls"
        action={<Pill tone="warn">step-up required</Pill>}
        id="controls"
      >
        <StepUpGate tier={3} action="Changing a storefront control">
          {controls.map((id) => {
            const state = query.data[id];
            const copy = COPY[id];
            const verified =
              state.verified_at === null
                ? null
                : state.verification_ok === true
                  ? `Verified ${state.enabled ? 'ON' : 'OFF'} on the storefront ${relativeAge(state.verified_at)} ago`
                  : 'The storefront did not match the last flip';

            return (
              <div key={id} className="o-kv" style={{ alignItems: 'flex-start' }}>
                <span className="o-kv__key" style={{ flex: 1 }}>
                  <strong className="o-section-title">{copy.title}</strong>
                  <br />
                  {copy.what}
                  <br />
                  <span className="o-muted">
                    {state.last_toggled_at
                      ? `Last flipped ${relativeAge(state.last_toggled_at)} ago by ${state.last_toggled_by ?? 'unknown'}`
                      : 'Never flipped from this console'}
                    {state.last_toggled_at ? ` (${fullTimestamp(state.last_toggled_at)})` : ''}
                  </span>
                  <br />
                  <span className={state.verification_ok === false ? 'o-field__error' : 'o-muted'}>
                    {verified ?? 'Not verified against the storefront yet'}
                  </span>
                </span>
                <span className="o-row">
                  <Pill tone={state.enabled ? 'ok' : 'neutral'}>{state.enabled ? 'ON' : 'OFF'}</Pill>
                  <Button
                    variant={state.enabled ? 'danger' : 'primary'}
                    onClick={() => setPending({ control: id, next: !state.enabled })}
                    disabled={
                      state.cooldown_until !== null &&
                      Date.parse(state.cooldown_until) > Date.now()
                    }
                  >
                    {state.enabled ? 'Turn off' : 'Turn on'}
                  </Button>
                </span>
              </div>
            );
          })}
        </StepUpGate>

        <p className="o-muted">
          Scheduled windows: BOGO is normally run as a window with a start and an end rather
          than a switch someone has to remember to flip back. The window is held in settings
          and the buttons here are the exception path. Ending a sale early gets the same
          ceremony as starting one.
        </p>
        <p className="o-muted">
          Power Mode is deliberately not here. Nobody could say what it changes on the
          storefront, and a control whose effect is unknown does not belong behind a
          confirmation that claims to list its effects.
        </p>
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title={
          pending
            ? `${pending.next ? 'Turn on' : 'Turn off'} ${COPY[pending.control].title}?`
            : ''
        }
        body={pending ? COPY[pending.control].what : ''}
        effects={pending ? COPY[pending.control].effects : []}
        confirmLabel={pending?.next ? 'Turn it on' : 'Turn it off'}
        typedConfirm={pending?.next ? 'ON' : 'OFF'}
        reasonRequired
        destructive={pending?.next === false}
        cooldownUntil={active?.cooldown_until ?? null}
        note="Writes an audit row, posts one line to Slack, then fetches the storefront to prove the change took. A mismatch raises a critical inbox item."
        onCancel={() => setPending(null)}
        onConfirm={async ({ reason }) => {
          const target = pending;
          setPending(null);
          if (!target) return;
          await toggle.mutateAsync({
            control: target.control,
            next: target.next,
            reason: reason ?? '',
          });
        }}
      />
    </>
  );
}
